import React from 'react';
import {
  FormattedDate, FormattedMessage, useIntl,
} from 'react-intl';
import {
  isLineItemCancelled, getCategoryName, isReviewGroupLead, getLineItemStateName, LINE_ITEM_STATES,
} from '@printdeal/business-logic';
import { Link, Link as GatsbyLink } from 'gatsby';
import { Button, Icon, Panel } from '@printdeal/ui-library';
import LineItemState from '../shared/Elements/LineItemState/LineItemState';
import { getFinalDeliveryDate } from '../../helpers/order-helper';
import LineItem from '../../../../types/cart/LineItem';
import Order from '../../../../types/order/Order';

import { Environment } from '../../../../helpers/env';
import DeliverFilesInList from './DeliverFiles/DeliverFilesInList';
import useSystemPages from '../../../../hooks/useSystemPages';

interface OrderListItemProps {
  lineItem: LineItem;
  order: Order;
}

const OrderLineItem = ({
  lineItem,
  order,
}: OrderListItemProps) => {
  const { locale } = useIntl();
  const [accountDashboard] = useSystemPages('accountDashboard');
  const { totalGroupQuantity, quantity } = lineItem;
  const lineItemQuantity = isReviewGroupLead(lineItem) ? totalGroupQuantity : quantity;
  const lineItemCustomFields = lineItem?.custom?.fields || {
    lineItemNumber: undefined,
    localizedProductName: undefined,
    mcpDocumentsSource: undefined,
    merchandisingProductName: undefined,
    productKey: undefined,
    userSelection: undefined,
    variableAttributes: undefined,
    revisedPromisedArrivalDateV2: undefined,
    revisedPromisedArrivalDate: undefined,
    promisedArrivalDate: undefined,
    proposalReviewInfo: undefined,
  };
  const deliveryDate = getFinalDeliveryDate({
    revisedPromisedArrivalDateV2: lineItemCustomFields.revisedPromisedArrivalDateV2,
    revisedPromisedArrivalDate: lineItemCustomFields.revisedPromisedArrivalDate,
    promisedArrivalDate: lineItemCustomFields.promisedArrivalDate,
  });
  const productName = getCategoryName(lineItem, locale);
  const proposalReviewInfo = JSON.parse(lineItemCustomFields.proposalReviewInfo || '{}') as { proposalId: string };
  const lineItemStateName = getLineItemStateName(lineItem, Environment);
  const hasActiveProposal = proposalReviewInfo?.proposalId
    && lineItemStateName === LINE_ITEM_STATES.DOC_REVIEW_PROOFING;

  return (
    <Panel className="pd-relative pd-flex pd-flex-col pd-gap-4 pd-p-0 lg:pd-flex-row">
      <div className="pd-order-2 pd-grow pd-px-5 lg:pd-px-0 lg:pd-py-5">
        {/* The link below with ::after pseudo-class allows the entire Card to be clickable
      and redirect to the order detail page. */}
        <Link
          to={`${order.id}?lineItemNumber=${lineItemCustomFields.lineItemNumber}`}
          data-cy="orderListItemLink"
          className="pd-font-bold after:pd-absolute after:pd-inset-0 after:pd-z-0 after:pd--m-px after:pd-rounded-md after:pd-border-primary-500 after:pd-content-[''] after:hover:pd-border-1"
        >
          {`${productName}, `}
          <FormattedMessage
            id="account.order.orderline.quantity"
            defaultMessage="{quantity} {quantity, plural, one {piece} other {pieces}}"
            values={{ quantity: lineItemQuantity }}
          />
        </Link>
        {!isLineItemCancelled(lineItem, Environment) && (
          <div className="pd-mt-1 pd-text-dim-500">
            <FormattedMessage
              id="account.order.deliveryDate"
              defaultMessage="Order delivery date"
            />
            {' '}
            <FormattedDate
              value={deliveryDate}
              year="numeric"
              month="long"
              day="2-digit"
            />
          </div>
        )}
        <LineItemState lineItem={lineItem} />
      </div>
      <div className="pd-z-10 pd-order-1 pd-w-min pd-shrink pd-pl-5 pd-pt-5 lg:pd-w-auto lg:pd-pl-0 lg:pd-pt-0">
        <DeliverFilesInList lineItem={lineItem} />
      </div>
      {hasActiveProposal && (
        <div className="pd-order-3 pd-flex pd-shrink pd-items-center pd-justify-start pd-px-5 pd-pb-5 lg:pd-justify-end lg:pd-pb-0">
          <Button as={GatsbyLink} to={`${accountDashboard?.path}/proposals/${proposalReviewInfo?.proposalId}`} className="pd-z-10">
            <FormattedMessage
              id="account.order.viewProposal"
              defaultMessage="View Design Proposal"
            />
          </Button>
        </div>
      )}
      <div className="pd-order-4 pd-flex pd-shrink pd-items-center pd-justify-end pd-pb-5 pd-pr-5 lg:pd-pb-0">
        <Icon size="sm" icon="chevron-right" />
      </div>
    </Panel>
  );
};

export default OrderLineItem;
